import React, { useState, useCallback } from 'react';
import { FaLink, FaTimes, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import api from '../../utils/api';

const LinkForm = ({ content, setContent, linkPreview, setLinkPreview }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchPreview = useCallback(async (url) => {
    if (!url.trim()) return;
    let normalizedUrl = url;
    if (!/^https?:\/\//i.test(normalizedUrl)) normalizedUrl = 'https://' + normalizedUrl;

    setLoadingPreview(true);
    try {
      const res = await api.get(`/links/preview?url=${encodeURIComponent(normalizedUrl)}`);
      setLinkPreview(res.data);
    } catch (e) {
      setLinkPreview({ url: normalizedUrl, title: normalizedUrl, description: '', image: '', domain: normalizedUrl.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] });
    } finally {
      setLoadingPreview(false);
    }
  }, [setLinkPreview]);

  const handleUrlKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); fetchPreview(linkUrl); }
  };

  return (
    <>
      <div>
        <label className="text-sm text-gray-600 mb-2 block">Your thoughts (Optional)</label>
        <textarea
          data-testid="link-caption-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg p-4 text-gray-900 placeholder:text-gray-400 transition-all outline-none min-h-[80px] resize-none"
          placeholder="What do you think about this link?"
        />
      </div>

      <div>
        <label className="text-sm text-gray-600 mb-2 block">Paste a URL</label>
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <FaLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={13} />
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={handleUrlKeyDown}
              onBlur={() => { if (linkUrl.trim() && !linkPreview) fetchPreview(linkUrl); }}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg text-gray-900 placeholder:text-gray-400 transition-all outline-none text-sm"
              placeholder="https://example.com/article"
              data-testid="link-url-input"
            />
          </div>
          <button
            type="button"
            onClick={() => fetchPreview(linkUrl)}
            disabled={!linkUrl.trim() || loadingPreview}
            className="px-4 py-2.5 bg-[#4A90E2] text-white rounded-lg text-sm font-medium hover:bg-[#3A80D2] disabled:opacity-50 transition-colors"
            data-testid="link-fetch-btn"
          >
            {loadingPreview ? <FaSpinner className="animate-spin" /> : 'Preview'}
          </button>
        </div>
      </div>

      {/* Link Preview Card */}
      {linkPreview && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid="link-preview-card">
          {linkPreview.image && (
            <div className="h-40 bg-gray-100 overflow-hidden">
              <img src={linkPreview.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{linkPreview.domain || linkPreview.site_name}</p>
                <p className="text-sm font-semibold text-gray-900 line-clamp-2">{linkPreview.title}</p>
                {linkPreview.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{linkPreview.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setLinkPreview(null); setLinkUrl(''); }}
                className="ml-2 text-gray-300 hover:text-red-500 p-1 flex-shrink-0"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LinkForm;
